import { Ban, Copy, Flag, Heart, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { DestinyBadge } from "@/components/karma/DestinyBadge";
import { ShopBundles } from "@/components/karma/ShopBundles";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useToast } from "@/hooks/use-toast";
import { computeMatchOverview, personalizeMatchOverview } from "@/lib/karma/match";
import { computeKarmaNumerology } from "@/lib/karma/numerology";
import { computeProfileCompletion } from "@/lib/karma/profile";
import { computeCoachTodaySummary } from "@/lib/karma/coach";
import { getProfileInsights } from "@/lib/karma/insights";
import { computeArchetype } from "@/lib/karma/archetypes";
import { getDestinyTheme, themedPanelClass } from "@/lib/karma/theme";
import {
  createReport,
  ensureStore,
  getMe,
  getUserByHandle,
  toggleBlock,
  toggleLike,
  updateUser,
} from "@/lib/karma/storage";
import { cn } from "@/lib/utils";

export default function KarmaProfile() {
  const { toast } = useToast();
  const { handle } = useParams();
  const [storeVersion, setStoreVersion] = useState(0);
  const [reportText, setReportText] = useState("");

  const store = useMemo(() => {
    storeVersion;
    return ensureStore();
  }, [storeVersion]);

  const me = getMe(store);
  const user = handle ? getUserByHandle(handle, store) : null;

  const profile = user ?? me;
  const numerology = useMemo(
    () => computeKarmaNumerology(profile.displayName, profile.birthDate),
    [profile.displayName, profile.birthDate],
  );

  const theme = getDestinyTheme(numerology.destinyNumber);

  const archetype = useMemo(() => computeArchetype(numerology), [numerology]);

  const profileInsights = useMemo(() => getProfileInsights(numerology), [numerology]);

  const coachStats = useMemo(
    () => computeCoachTodaySummary(profile, new Date()),
    [profile],
  );

  const matchOverview = useMemo(() => {
    if (!user) return null;
    const meNumerology = computeKarmaNumerology(me.displayName, me.birthDate);
    const overview = computeMatchOverview(meNumerology, numerology);
    return personalizeMatchOverview(overview, {
      meName: me.displayName,
      otherName: profile.displayName,
    });
  }, [me.displayName, me.birthDate, numerology, profile.displayName, user]);

  const photos =
    profile.photos.length > 0
      ? profile.photos
      : [{ id: "p", url: "/placeholder.svg", addedAt: profile.createdAt }];

  const primaryPhoto = photos[0];
  const thumbnailPhotos = photos.slice(1, 7);

  const completion = useMemo(() => computeProfileCompletion(profile), [profile]);

  const profileLink = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/karma/u/${profile.handle}`;
  }, [profile.handle]);

  return (
    <div className="space-y-8">
      <div className={themedPanelClass(numerology.destinyNumber, "p-5")}> 
        <div className={cn("pointer-events-none absolute inset-0 bg-gradient-to-br", theme.gradient)} />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-4 w-4" />
              Profil
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">
              {profile.displayName}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <DestinyBadge destiny={numerology.destinyNumber} label="Kader" />
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                Arketip: {archetype.name}
              </span>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                PIN: {numerology.pinText}
              </span>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                {profile.matchEnabled ? "Eşleşmeye açık" : "Eşleşme kapalı"}
              </span>
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                Seviye {coachStats.level.level}
              </span>
              {coachStats.titles.slice(0, 1).map((t) => (
                <span
                  key={t.id}
                  className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                  title={t.description}
                >
                  {t.name}
                </span>
              ))}
            </div>
            <p className="max-w-2xl text-sm text-muted-foreground">
              {profile.bio || "Kendini tanıt, niyetini yaz, enerjini paylaş."}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/">Keşif</Link>
            </Button>

            <Button
              variant="outline"
              className="rounded-full"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(profileLink);
                  toast({ title: "Kopyalandı", description: "Profil bağlantısı panoya kopyalandı." });
                } catch {
                  toast({
                    title: "Kopyalanamadı",
                    description: "Tarayıcı panoya kopyalamaya izin vermedi.",
                  });
                }
              }}
            >
              <Copy className="mr-2 h-4 w-4" /> Paylaş
            </Button>

            {profile.id === me.id ? (
              <>
                <Button asChild className="rounded-full">
                  <Link to="/karma/me">Panel</Link>
                </Button>

                <div className="flex items-center justify-between gap-3 rounded-full border bg-background/70 px-4 py-2 backdrop-blur">
                  <div className="grid gap-0.5">
                    <div className="text-xs font-medium">Eşleşme</div>
                    <div className="text-[11px] text-muted-foreground">
                      {profile.matchEnabled ? "Açık" : "Kapalı (Koç Modu)"}
                    </div>
                  </div>
                  <Switch
                    checked={profile.matchEnabled}
                    onCheckedChange={(checked) => {
                      updateUser({ ...me, matchEnabled: checked });
                      setStoreVersion((v) => v + 1);
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <Button
                  className="rounded-full"
                  variant="secondary"
                  onClick={() => {
                    toggleLike(profile.id);
                    setStoreVersion((v) => v + 1);
                  }}
                >
                  <Heart className="mr-2 h-4 w-4" /> Beğen ({profile.likes})
                </Button>

                <Button
                  className="rounded-full"
                  variant="outline"
                  onClick={() => {
                    toggleBlock(profile.id);
                    setStoreVersion((v) => v + 1);
                    toast({ title: "Güncellendi", description: "Engel listesi güncellendi." });
                  }}
                >
                  <Ban className="mr-2 h-4 w-4" /> Engelle
                </Button>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="rounded-full" variant="outline">
                      <Flag className="mr-2 h-4 w-4" /> Şikayet
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Şikayet</DialogTitle>
                      <DialogDescription>
                        Bu şikayet sadece bu cihazda saklanır (demo). İstersen sebebini yaz.
                      </DialogDescription>
                    </DialogHeader>
                    <Textarea
                      value={reportText}
                      onChange={(e) => setReportText(e.target.value)}
                      placeholder="Örn: Uygunsuz fotoğraf"
                      rows={4}
                    />
                    <DialogFooter>
                      <Button
                        onClick={() => {
                          createReport({ targetUserId: profile.id, reason: reportText });
                          setReportText("");
                          setStoreVersion((v) => v + 1);
                          toast({ title: "Kaydedildi", description: "Şikayet kaydedildi." });
                        }}
                      >
                        Gönder
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>
      </div>

      {profile.id === me.id ? (
        <Card className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-display text-lg font-semibold tracking-tight">Profil tamamlanma</div>
              <div className="mt-1 text-sm text-muted-foreground">
                %{completion.percent} — {completion.missing.length ? "Eksikler: " + completion.missing.join(", ") : "Hazır"}
              </div>
            </div>
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/karma/me">Düzenle</Link>
            </Button>
          </div>
          <div className="mt-4">
            <Progress value={completion.percent} />
          </div>
        </Card>
      ) : null}

      <section className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="md:col-span-1 lg:col-span-2">
          <div className="p-5">
            <div className="font-display text-lg font-semibold tracking-tight">Fotoğraflar</div>

            <div className="mt-4 overflow-hidden rounded-3xl border bg-muted">
              <div className="aspect-[4/3] w-full sm:aspect-[16/10]">
                <img
                  src={primaryPhoto?.url || "/placeholder.svg"}
                  alt={profile.displayName}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {thumbnailPhotos.length ? (
              <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
                {thumbnailPhotos.map((p) => (
                  <div
                    key={p.id}
                    className="aspect-square overflow-hidden rounded-2xl border bg-muted"
                  >
                    <img
                      src={p.url}
                      alt={profile.displayName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <div className="font-display text-lg font-semibold tracking-tight">Numeroloji Özeti</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Romantik ilişki, arkadaşlık ya da iş birliği için kişisel özet.
            </div>
            <Separator className="my-4" />
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-[1fr_auto] items-baseline gap-4">
                <span className="text-muted-foreground">Kader yolu</span>
                <span className="text-right font-medium">{numerology.destinyPath}</span>
              </div>
              <div className="grid grid-cols-[1fr_auto] items-baseline gap-4">
                <span className="text-muted-foreground">Kader sayısı</span>
                <span className="text-right font-medium">
                  {numerology.lifePathMaster ? (
                    <span>
                      {numerology.lifePathMaster}→{numerology.destinyNumber}
                    </span>
                  ) : (
                    numerology.destinyNumber
                  )}
                </span>
              </div>
              <div className="grid grid-cols-[1fr_auto] items-baseline gap-4">
                <span className="text-muted-foreground">Kalp arzusu</span>
                <span className="text-right font-medium">{numerology.heartDesireNumber}</span>
              </div>
              <div className="grid grid-cols-[1fr_auto] items-baseline gap-4">
                <span className="text-muted-foreground">İfade</span>
                <span className="text-right font-medium">{numerology.expressionNumber}</span>
              </div>
              <div className="grid grid-cols-[1fr_auto] items-baseline gap-4">
                <span className="text-muted-foreground">Karmik kodlar</span>
                <span className="text-right font-medium">
                  {numerology.karmicCodes.length ? numerology.karmicCodes.join(", ") : "—"}
                </span>
              </div>
              <div className="grid grid-cols-[1fr_auto] items-baseline gap-4">
                <span className="text-muted-foreground">Beğeni</span>
                <span className="text-right font-medium">{profile.likes}</span>
              </div>
            </div>

            {matchOverview ? (
              <div className="mt-6 rounded-xl border bg-muted/30 p-4">
                <div className="text-sm font-semibold">Seninle Uyum (ilişki / arkadaşlık / iş)</div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    {matchOverview.categoryLabel}
                  </span>
                  <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                    Ortak yol: {matchOverview.relationshipNumber}
                  </span>
                  <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                    Uyum: <span className="font-semibold">{matchOverview.scores.dailyHarmony}</span>/100
                  </span>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-xs font-medium text-muted-foreground">Kimya</div>
                    <div className="mt-1 text-lg font-semibold">
                      {Math.round(matchOverview.scores.attraction / 10)}/10
                    </div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-xs font-medium text-muted-foreground">Günlük uyum</div>
                    <div className="mt-1 text-lg font-semibold">
                      {Math.round(matchOverview.scores.dailyHarmony / 10)}/10
                    </div>
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-3">
                    <div className="text-xs font-medium text-muted-foreground">Uzun vade</div>
                    <div className="mt-1 text-lg font-semibold">
                      {Math.round(matchOverview.scores.longevity / 10)}/10
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                  {matchOverview.notes.map((n) => (
                    <div key={n}>• {n}</div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </Card>

        <Card className="md:col-span-2 lg:col-span-3">
          <div className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg font-semibold tracking-tight">Kısa İçgörüler</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Güçlü taraf, zorlanma ve mini pratik.
                </div>
              </div>
              <div className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                {profile.id === me.id ? "Sen" : "Profil"}
              </div>
            </div>

            <Separator className="my-4" />

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {profileInsights.map((it) => (
                <div key={it.id} className="rounded-xl border bg-muted/30 p-4">
                  <div className="text-sm font-semibold">{it.title}</div>
                  <div className="mt-2 text-sm text-muted-foreground">{it.body}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight">Kişiye özel Spiritüel Dükkan</h2>
          <p className="text-sm text-muted-foreground">
            Karmik kodlarına göre meditasyon, doğal taş ve olumlamalar.
          </p>
        </div>
        <ShopBundles codes={numerology.karmicCodes} />
      </section>
    </div>
  );
}
